import { createApiClient, apiClient } from "../../api/apiClient";
import { handleRackingInspectionErrorWithFriendlyMessage } from '../../store/utils/rackingInspectionErrorHandler';

export interface RackingInspectionQuestion {
  ROWKEY: number;
  Section: string;
  QNUM: number;
  QText: string;
  SHORT_FORM: string | null;
}

interface FetchRackingInspectionQuestionsOptions {
  isCompletedInspection?: boolean;
  recordId?: string | null;
}

// Module-level cache/guard to avoid repeated API calls when the endpoint fails
let _cachedQuestions: RackingInspectionQuestion[] | null = null;
let _hasAttemptedFetch = false;

/**
 * Reset the module-level cache to allow fresh API calls
 * Call this when you want to force a new fetch (e.g., after clearing Redux questions)
 */
export const resetRackingInspectionQuestionsCache = () => {
  console.log('[Racking Inspection] Resetting questions cache');
  _cachedQuestions = null;
  _hasAttemptedFetch = false;
};

/**
 * Fetches racking inspection questions from the API
 * @param options Completed/in-progress request options
 * @returns Promise<RackingInspectionQuestion[]>
 */
export const fetchRackingInspectionQuestions = async (
  options: boolean | FetchRackingInspectionQuestionsOptions = false
): Promise<
  RackingInspectionQuestion[]
> => {
  const normalizedOptions: FetchRackingInspectionQuestionsOptions =
    typeof options === "boolean"
      ? { isCompletedInspection: options }
      : options;

  const isCompletedInspection = normalizedOptions.isCompletedInspection ?? false;
  const recordId = normalizedOptions.recordId;
  const shouldIncludeRecordId = !isCompletedInspection && !!recordId;

  // If we've already attempted a fetch this session, return cached result (even if empty)
  // For in-progress inspections with recordId, always call API so backend receives record context.
  if (_hasAttemptedFetch && !shouldIncludeRecordId) {
    return _cachedQuestions || [];
  }

  const api = createApiClient();
  // Use the standardized endpoint name (keep consistent with other components)
  const endpoint = shouldIncludeRecordId
    ? `/racking-inspection/questions?recordId=${encodeURIComponent(String(recordId))}`
    : "/racking-inspection/questions";

  // Only skip org context params for COMPLETED inspections
  // For new or in-progress inspections, include org context to filter questions by location
  const config = isCompletedInspection ? { _skipOrgContextForQuestions: true } : undefined;

  console.log(
    "[Racking Inspection] Fetching questions - isCompletedInspection:",
    isCompletedInspection,
    "recordId:",
    shouldIncludeRecordId ? recordId : "none"
  );

  try {
    const response = await apiClient(api, endpoint, "GET", undefined, config);

    // If API returned an application-level error payload, show centralized overlay and return empty
    if (
      (response && (response as any).status && (response as any).status !== 'success') ||
      (response && ((response as any).error || (response as any).message))
    ) {
      try {
        handleRackingInspectionErrorWithFriendlyMessage(
          'fetchRackingInspectionQuestions',
          response,
          true,
          { endpoint }
        );
      } catch (e) {
        // swallow
      }

      // Cache only generic requests (no recordId).
      if (!shouldIncludeRecordId) {
        _hasAttemptedFetch = true;
        _cachedQuestions = [];
      }
      return [];
    }

    if (!response) {
      if (!shouldIncludeRecordId) {
        _hasAttemptedFetch = true;
        _cachedQuestions = [];
      }
      return [];
    }

    // If response is an object with keys, convert to array
    let questions: RackingInspectionQuestion[];
    if (typeof response === "object" && !Array.isArray(response)) {
      questions = Object.values(response) as RackingInspectionQuestion[];
    } else {
      questions = response as RackingInspectionQuestion[];
    }

    // Cache successful result only for generic requests.
    if (!shouldIncludeRecordId) {
      _cachedQuestions = questions;
      _hasAttemptedFetch = true;
    }

    return questions;
  } catch (error: any) {
    try {
      handleRackingInspectionErrorWithFriendlyMessage(
        'fetchRackingInspectionQuestions',
        error,
        true,
        { endpoint }
      );
    } catch (e) {
      // swallow
    }
    console.error("Error fetching racking inspection questions:", error);

    // Cache empty result only for generic requests.
    if (!shouldIncludeRecordId) {
      _hasAttemptedFetch = true;
      _cachedQuestions = [];
    }

    return [];
  }
};
